import { motion, AnimatePresence } from 'motion/react'
import {
  Wrench, ShieldCheck, ShieldX, Mail, Sun,
  Lightbulb, BookOpenText, Link, Unlink, UserCog,
  Repeat, Puzzle, Activity,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ActivityItem, ActivityEventType } from '../types'
import { AnimatedChevron, DataRow } from '@tendbot/ui'
import { expandCollapse } from '@tendbot/ui'
import { relativeTime } from '@tendbot/ui'
import { normalizeToolName } from '../../../shared/utils/tools'

/** Look up a translated label for a snake_case tool/action name, with fallback */
function prettyName(snakeName: string, t: (key: string, opts?: { defaultValue: string }) => string): string {
  const normalized = normalizeToolName(snakeName)
  return t(`toolLabel.${normalized}`, {
    defaultValue: normalized.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
  })
}

interface EventMeta {
  icon: LucideIcon
  borderColor: string
  iconBg: string
  iconColor: string
}

function getEventMeta(item: ActivityItem): EventMeta {
  const type = item.eventType
  const decision = item.metadata.decision as string | undefined

  const fallback: EventMeta = {
    icon: Activity,
    borderColor: 'border-l-pearl/30',
    iconBg: 'bg-pearl/5',
    iconColor: 'text-pearl/50',
  }

  const map: Partial<Record<ActivityEventType, EventMeta>> = {
    tool_call: {
      icon: Wrench,
      borderColor: 'border-l-pearl/40',
      iconBg: 'bg-pearl/8',
      iconColor: 'text-pearl/70',
    },
    approval_resolved: decision === 'denied'
      ? {
          icon: ShieldX,
          borderColor: 'border-l-rose',
          iconBg: 'bg-rose/10',
          iconColor: 'text-rose',
        }
      : {
          icon: ShieldCheck,
          borderColor: 'border-l-emerald',
          iconBg: 'bg-emerald/10',
          iconColor: 'text-emerald',
        },
    email_sent: {
      icon: Mail,
      borderColor: 'border-l-gold',
      iconBg: 'bg-gold/10',
      iconColor: 'text-gold',
    },
    briefing_delivered: {
      icon: Sun,
      borderColor: 'border-l-amber',
      iconBg: 'bg-amber/10',
      iconColor: 'text-amber',
    },
    nudge_delivered: {
      icon: Lightbulb,
      borderColor: 'border-l-indigo-400',
      iconBg: 'bg-indigo-400/10',
      iconColor: 'text-indigo-400',
    },
    notebook_change: {
      icon: BookOpenText,
      borderColor: 'border-l-violet-400',
      iconBg: 'bg-violet-400/10',
      iconColor: 'text-violet-400',
    },
    plugin_tool_call: {
      icon: Puzzle,
      borderColor: 'border-l-pearl/40',
      iconBg: 'bg-pearl/8',
      iconColor: 'text-pearl/70',
    },
    followup_change: {
      icon: Repeat,
      borderColor: 'border-l-gold',
      iconBg: 'bg-gold/10',
      iconColor: 'text-gold',
    },
    oauth_connect: {
      icon: Link,
      borderColor: 'border-l-emerald',
      iconBg: 'bg-emerald/10',
      iconColor: 'text-emerald',
    },
    oauth_disconnect: {
      icon: Unlink,
      borderColor: 'border-l-rose',
      iconBg: 'bg-rose/10',
      iconColor: 'text-rose',
    },
    profile_update: {
      icon: UserCog,
      borderColor: 'border-l-pearl/40',
      iconBg: 'bg-pearl/8',
      iconColor: 'text-pearl/70',
    },
    account_deleted: {
      icon: UserCog,
      borderColor: 'border-l-rose',
      iconBg: 'bg-rose/10',
      iconColor: 'text-rose',
    },
  }

  return map[type] ?? fallback
}

function getTitle(item: ActivityItem, t: (key: string, opts?: { defaultValue: string }) => string): string {
  const m = item.metadata
  switch (item.eventType) {
    case 'tool_call':
      return prettyName(String(m.toolName ?? ''), t)
    case 'approval_resolved':
      return `${prettyName(String(m.actionType), t)} — ${t(`outcome.${m.decision}`)}`
    case 'email_sent':
      return `${t('eventType.email_sent')} — ${m.to}`
    case 'briefing_delivered':
      return t('eventType.briefing_delivered')
    case 'nudge_delivered':
      return `${t('eventType.nudge_delivered')}: ${m.title}`
    case 'notebook_change': {
      const action = t(`notebookAction.${m.action}`, { defaultValue: String(m.action) })
      return `${t('eventType.notebook_change')}: ${action} ${m.title}`
    }
    case 'plugin_tool_call':
      return prettyName(String(m.toolName ?? ''), t)
    case 'followup_change':
      return `${t('eventType.followup_change', { defaultValue: 'Follow-up' })}: ${t(`followupAction.${m.action}`, { defaultValue: String(m.action ?? '') })}`
    case 'oauth_connect':
      return `${t('eventType.oauth_connect', { defaultValue: 'Connected' })}: ${m.provider}`
    case 'oauth_disconnect':
      return `${t('eventType.oauth_disconnect', { defaultValue: 'Disconnected' })}: ${m.provider}`
    case 'profile_update':
      return t('eventType.profile_update', { defaultValue: 'Profile updated' })
    case 'account_deleted':
      return t('eventType.account_deleted', { defaultValue: 'Account deleted' })
    default:
      return item.eventType
  }
}

function getSubtitle(item: ActivityItem, t: (key: string, opts?: { defaultValue: string }) => string): string | null {
  const m = item.metadata
  switch (item.eventType) {
    case 'tool_call':
      return m.query ? String(m.query) : m.eventTitle ? String(m.eventTitle) : null
    case 'approval_resolved':
      return m.summary ? String(m.summary) : null
    case 'email_sent':
      return m.subject ? String(m.subject) : null
    case 'briefing_delivered': {
      const sections = m.sections as string[] | undefined
      return sections ? sections.join(', ') : null
    }
    case 'nudge_delivered':
      return `${t(`detail.priority`)}: ${m.priority}`
    case 'notebook_change':
      return null
    case 'plugin_tool_call':
      return m.pluginId ? String(m.pluginId) : null
    case 'followup_change':
      return m.summary ? String(m.summary) : null
    case 'oauth_connect':
    case 'oauth_disconnect':
      return m.pluginId ? String(m.pluginId) : null
    case 'profile_update': {
      const fields = m.fields as string[] | undefined
      return fields ? fields.join(', ') : null
    }
    default:
      return null
  }
}

interface Props {
  item: ActivityItem
  isExpanded: boolean
  onToggle: () => void
}

export default function ActivityCard({ item, isExpanded, onToggle }: Props) {
  const { t } = useTranslation('activity')
  const meta = getEventMeta(item)
  const Icon = meta.icon
  const title = getTitle(item, t)
  const subtitle = getSubtitle(item, t)
  const outcome = item.metadata.outcome as string | undefined
  const durationMs = item.metadata.durationMs as number | undefined

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card rounded-xl overflow-hidden border border-white/5 border-l-2 ${meta.borderColor} transition-colors hover:border-white/10`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 group"
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-8 h-8 rounded-lg ${meta.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
            <Icon size={15} className={meta.iconColor} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-ivory text-sm font-medium truncate">{title}</p>
              <span className="text-pearl/60 text-xs whitespace-nowrap shrink-0">
                {relativeTime(item.createdAt)}
              </span>
            </div>

            {/* Subtitle + badges row */}
            <div className="flex items-center gap-2 mt-1">
              {subtitle && (
                <p className="text-pearl/50 text-xs truncate">{subtitle}</p>
              )}
              {item.eventType === 'tool_call' && outcome && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider shrink-0 ${
                  outcome === 'success'
                    ? 'bg-emerald/15 text-emerald'
                    : 'bg-rose/15 text-rose'
                }`}>
                  {t(`outcome.${outcome}`)}
                </span>
              )}
              {durationMs != null && (
                <span className="text-pearl/55 text-[10px] shrink-0">{durationMs}ms</span>
              )}
            </div>
          </div>

          {/* Chevron */}
          <AnimatedChevron
            isOpen={isExpanded}
            size={14}
            className="text-pearl/45 group-hover:text-pearl/70 transition-colors mt-1 shrink-0"
          />
        </div>
      </button>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            {...expandCollapse}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <div className="pt-3 border-t border-white/5 space-y-2">
                <DetailRows item={item} t={t} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function DetailRows({ item, t }: { item: ActivityItem; t: (key: string, opts?: { defaultValue: string }) => string }) {
  const m = item.metadata
  const rows: { label: string; value: string }[] = []

  switch (item.eventType) {
    case 'tool_call':
      rows.push({ label: t('detail.toolName'), value: prettyName(String(m.toolName), t) })
      if (m.outcome) rows.push({ label: t('detail.action'), value: String(m.outcome) })
      if (m.durationMs != null) rows.push({ label: t('detail.duration'), value: `${m.durationMs}ms` })
      if (m.query) rows.push({ label: t('detail.query'), value: String(m.query) })
      if (m.eventsReturned != null) rows.push({ label: t('detail.events'), value: String(m.eventsReturned) })
      if (m.resultsReturned != null) rows.push({ label: t('detail.results'), value: String(m.resultsReturned) })
      if (m.eventTitle) rows.push({ label: t('detail.entry'), value: String(m.eventTitle) })
      if (m.error) rows.push({ label: t('detail.error'), value: String(m.error) })
      break
    case 'approval_resolved':
      if (m.actionType) rows.push({ label: t('detail.action'), value: prettyName(String(m.actionType), t) })
      if (m.decision) rows.push({ label: t('detail.decision'), value: t(`outcome.${m.decision}`) })
      if (m.summary) rows.push({ label: t('detail.subject'), value: String(m.summary) })
      break
    case 'email_sent':
      if (m.to) rows.push({ label: t('detail.recipient'), value: String(m.to) })
      if (m.subject) rows.push({ label: t('detail.subject'), value: String(m.subject) })
      if (m.action) rows.push({ label: t('detail.action'), value: t(`emailAction.${m.action}`, { defaultValue: String(m.action) }) })
      break
    case 'briefing_delivered': {
      const sections = m.sections as string[] | undefined
      if (sections) rows.push({ label: t('detail.sections'), value: sections.join(', ') })
      if (m.emailCount != null) rows.push({ label: t('detail.emails'), value: String(m.emailCount) })
      if (m.meetingCount != null) rows.push({ label: t('detail.meetings'), value: String(m.meetingCount) })
      break
    }
    case 'nudge_delivered':
      if (m.triggerType) rows.push({ label: t('detail.trigger'), value: String(m.triggerType) })
      if (m.priority) rows.push({ label: t('detail.priority'), value: String(m.priority) })
      if (m.title) rows.push({ label: t('detail.subject'), value: String(m.title) })
      break
    case 'notebook_change':
      if (m.action) rows.push({ label: t('detail.action'), value: String(m.action) })
      if (m.title) rows.push({ label: t('detail.entry'), value: String(m.title) })
      break
    case 'plugin_tool_call':
      if (m.pluginId) rows.push({ label: t('detail.plugin', { defaultValue: 'Plugin' }), value: String(m.pluginId) })
      rows.push({ label: t('detail.toolName'), value: prettyName(String(m.toolName), t) })
      if (m.outcome) rows.push({ label: t('detail.action'), value: String(m.outcome) })
      if (m.durationMs != null) rows.push({ label: t('detail.duration'), value: `${m.durationMs}ms` })
      break
    case 'followup_change':
      if (m.action) rows.push({ label: t('detail.action'), value: String(m.action) })
      if (m.summary) rows.push({ label: t('detail.subject'), value: String(m.summary) })
      break
    case 'oauth_connect':
    case 'oauth_disconnect':
      if (m.provider) rows.push({ label: t('detail.provider', { defaultValue: 'Provider' }), value: String(m.provider) })
      if (m.pluginId) rows.push({ label: t('detail.plugin', { defaultValue: 'Plugin' }), value: String(m.pluginId) })
      break
    case 'profile_update': {
      const fields = m.fields as string[] | undefined
      if (fields) rows.push({ label: t('detail.fields', { defaultValue: 'Fields' }), value: fields.join(', ') })
      break
    }
    case 'account_deleted':
      break
  }

  return (
    <>
      {rows.map(row => (
        <DataRow key={row.label} label={row.label} value={row.value} />
      ))}
      <DataRow label={t('detail.source')} value={item.sourceModule} valueClassName="text-pearl/60" />
    </>
  )
}
